import { Block } from 'baseui/block';
import { Button } from 'baseui/button';
import { Input, MaskedInput, SIZE } from 'baseui/input';
import { Modal, ModalBody, ModalButton, ModalFooter, ModalHeader } from 'baseui/modal';
import { KIND, Notification } from 'baseui/notification';
import { StatefulPopover } from 'baseui/popover';
import { ALIGN, Radio, RadioGroup } from 'baseui/radio';
import { Select } from 'baseui/select';
import { HeadingSmall, Label1, Paragraph1 } from 'baseui/typography';
import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { startConfirmModal } from '../../actions/ui';
import { setAllAnswers, setAnswer } from '../../actions/userData';
import ArrowButton from '../../onboard/ArrowButton';
import EndableIncome from '../../onboard/Question/EndableIncome';
import { GeneralExpenses, ShortTermDebts } from '../../onboard/Question/GeneralExpenses';
import IncomeTableInput from '../../onboard/Question/IncomeTableInput';
import InvestmentTableInput from '../../onboard/Question/InvestmentTableInput';
import { validateBoolean } from '../flows/validate/booleanValidator';
import ConfirmChangeModal from './ConfirmChangeModal';
import { Payroll } from './Payroll';

export const BooleanInput = ({ setAnswer, currentAnswer, question }) => {
    currentAnswer === true && (currentAnswer = 'true');
    currentAnswer === false && (currentAnswer = 'false');

    return (
        <RadioGroup
            onChange={e => {
                let newValue = e.target.value;
                newValue === 'true' && (newValue = true);
                newValue === 'false' && (newValue = false);
                setAnswer(newValue)
            }}
            name={question.id}
            align={ALIGN.horizontal}
            value={currentAnswer || undefined}
        >
            <Radio value="true">Yes</Radio>
            <Radio
                value={"false"}
            >
                No
            </Radio>
        </RadioGroup>
    );

}

const DateInput = ({ setAnswer, currentAnswer, question }) => {
    return (
        <MaskedInput
            onChange={(e) => setAnswer(e.target.value)}
            placeholder='dd/mm/yyyy'
            mask='99/99/9999'
            value={currentAnswer || ''}
            name={question.id}
        />
    );
}

const SelectInput = ({ setAnswer, currentAnswer, question }) => {
    return <Select options={question.options} value={currentAnswer} onChange={(e) => setAnswer(e.value)} />
}

export const TextInput = ({ setAnswer, currentAnswer, question }) => {
    return <Input
        value={currentAnswer || ''}
        onChange={(e) => setAnswer(e.target.value)}
        name={question.id}
        size={SIZE.large}
    />
}

const NumberInput = ({ setAnswer, currentAnswer, question }) => {
    React.useEffect(() => {
        if (currentAnswer === undefined) {
            setAnswer('0')
        }
    }, [])

    return <Input
        value={currentAnswer || ''}
        onChange={(e) => setAnswer(e.target.value)}
        name={question.id}
        size={SIZE.large}
        type='number'
    />
}

const PercentageInput = ({ setAnswer, currentAnswer, question }) => {
    return <Input
        value={currentAnswer || ''}
        onChange={(e) => setAnswer(e.target.value)}
        name={question.id}
        size={SIZE.large}
        type='number'
        endEnhancer='%'
    />
}

const DollarInput = ({ setAnswer, currentAnswer, question }) => {

    React.useEffect(() => {
        if (currentAnswer === undefined) {
            setAnswer("0")
        }
    }, [])

    return <Input
        value={currentAnswer || ''}
        onChange={(e) => setAnswer(e.target.value)}
        name={question.id}
        size={SIZE.large}
        type='number'
        endEnhancer='.00'
        startEnhancer='$'
    />
}

const DollarOrPercent = ({ setAnswer, currentAnswer, question }) => {
    const handleSetValue = (value, type) => setAnswer({ [type]: value });
    return <div style={{ width: "100%", display: "flex", flexFlow: "row nowrap" }}>
        <PercentageInput setAnswer={(value) => handleSetValue(value, 'percent')} question={question} currentAnswer={currentAnswer?.percent} />
        <Paragraph1 overrides={{ Block: { style: () => ({ margin: "20px" }) } }}>or</Paragraph1>
        <DollarInput setAnswer={(value) => handleSetValue(value, 'dollar')} question={question} currentAnswer={currentAnswer?.dollar} />
    </div>;
}

const InvestmentTypePicker = ({ setAnswer, currentAnswer, question }) => {
    const makeLabel = value => {
        switch (value) {
            case 'NQ':
                return 'Non-Retirement';
            case 'Q':
                return 'Retirement';
            case 'TF':
                return 'Roth';
            default:
                break;
        }
    }
    const initialValue = currentAnswer ? [{ label: makeLabel(currentAnswer), id: currentAnswer }] : '';
    const [value, setValue] = React.useState(initialValue);
    return (<>
        <Select
            options={[
                { label: "Non-Retirement", id: "NQ" },
                { label: "Retirement", id: "Q" },
                { label: "Roth", id: "TF" },
            ]}
            value={value}
            placeholder="Select Type"

            onChange={(params) => {
                setValue(params.value)
                setAnswer(params.value[0]?.id)
            }}
            size={SIZE.large}
        />
    </>
    );
}

const PensionComponent = ({ setAnswer, currentAnswer, question }) => {
    return <BooleanInput setAnswer={(v) => setAnswer(!v)} currentAnswer={!currentAnswer} question={question} />
}

const EndableIncomeComponent = ({ setAnswer, currentAnswer, question }) => {
    return <EndableIncome updateInput={(v) => setAnswer(v)} value={currentAnswer !== undefined ? currentAnswer : { incomeAmount: '' }} id={question.id} />
}

const HelpButton = ({ content }) => {
    return <StatefulPopover placement='left' content={() => <Block padding={"20px"} maxWidth={"30vw"}>{content}</Block>}><Button shape='round' overrides={{ BaseButton: { style: () => ({ marginTop: 0, marginRight: 0, marginBottom: 10, paddingTop: 12, paddingRight: 16, paddingBottom: 12, paddingLeft: 16 }) } }}>?</Button></StatefulPopover>;
}

const ConnectedIncome = ({ setAnswer, currentAnswer, question }) => {
    return <IncomeTableInput updateInput={(v) => setAnswer(v)} value={currentAnswer !== undefined ? currentAnswer : []} id={question.id} question={question} />
}

const questionInputMapping = {
    'boolean': {
        Component: BooleanInput,
        validator: validateBoolean
    },
    'string': {
        Component: TextInput,
        validator: (value) => true
    },
    'number': {
        Component: NumberInput,
        validator: (value) => true
    },
    'dollar': {
        Component: DollarInput,
        validator: (value) => value !== undefined && value !== '' && value >= 0
    },
    'percentage': {
        Component: PercentageInput,
        validator: (value) => true
    },
    'dollarOrPercent': {
        Component: DollarOrPercent,
        validator: (value) => true
    },
    'select': {
        Component: SelectInput,
        validator: (value) => true
    },
    'dob': {
        Component: DateInput,
        validator: (value) => true
    },
    'investmentTable': {
        Component: InvestmentTableInput,
        validator: (value) => true
    },
    'investmentTypePicker': {
        Component: InvestmentTypePicker,
        validator: (value) => true
    },
    'payrollDeductions': {
        Component: Payroll.Component,
        validator: (value) => true
    },
    'pension': {
        Component: PensionComponent,
        validator: (value) => true
    },
    'endableIncome': {
        Component: EndableIncomeComponent,
        validator: (value) => true
    },
    'shortTermDebts': {
        Component: ShortTermDebts,
        validator: (value) => true
    },
    'generalExpenses': {
        Component: GeneralExpenses,
        validator: (value) => true
    },
    'incomeTable': {
        Component: ConnectedIncome,
        validator: (value) => true
    },
    'wrongBoolean': {
        Component: PensionComponent,
        validator: (value) => true
    }
}

const Flow = ({ questionSections, sectionValidator, answers, setAnswer, setAllAnswers, flowMode, finishFlow, goBack, startConfirmModal }) => {
    const [errors, setErrors] = React.useState({});

    function validate() {
        let newErrors = {}
        questionSections.forEach(section => {
            if (section.shouldShow(answers)) {
                section.questions.forEach(question => {
                    const inputValidator = questionInputMapping[question.type]?.validator;
                    if (inputValidator && !inputValidator(answers[question.id], answers)) newErrors[question.id] = 'Error';
                    if (sectionValidator) {
                        const { isValid, errors } = sectionValidator(answers);
                        if (!isValid) errors.forEach(error => newErrors[error.location] = error.message);
                    }
                });
            }
        });

        if (!_.isEmpty(newErrors)) {
            setErrors(newErrors);
            return false;
        }
        return true;
    }

    return <div style={{ width: '100%' }}>
        <div style={{ maxWidth: '700px', width: '95%', margin: 'auto' }}>
            {questionSections.map((section, sectionIndex) => {
                if (!section.shouldShow(answers)) {
                    return undefined;
                }
                let sectionText = flowMode ? section.text : section.descriptor;
                return <div key={'section-' + sectionIndex}>
                    {sectionText ? <HeadingSmall>{sectionText}</HeadingSmall> : undefined}
                    {section.questions.map((question, index) => {
                        if (questionInputMapping[question.type]) {
                            let InputComponent = questionInputMapping[question.type].Component;
                            return <div style={{ width: '100%', display: "flex", flexDirection: "column" }} key={index + '-flow-' + question.id}>
                                {question.text || question.description ? <div style={{ width: '100%', display: "flex", justifyContent: "space-between" }}>
                                    <Label1 $style={{ float: 'left', marginTop: '15px', marginBottom: '5px' }}>{flowMode ? question.text : question.description}</Label1>
                                    <div style={{ float: 'right', marginTop: '15px', marginBottom: '5px' }}>{question.help ? <HelpButton content={question.help} /> : undefined}</div>
                                </div> : undefined}
                                {errors[question.id] ? <Notification kind={KIND.negative}>{() => errors[question.id]}</Notification> : undefined}
                                {question.explaination ? <div style={{ border: "4px solid #EEEEEE", marginBottom: "20px" }}>{question.explaination}</div> : null}
                                <div><InputComponent question={question} currentAnswer={answers[question.id]} setAnswer={(v) => {
                                    if (question.onChange && answers[question.id] !== undefined) {
                                        question.onChange(v, answers, setAllAnswers, startConfirmModal);
                                    } else {
                                        setAnswer(question.id, v);
                                    }
                                }} /></div>
                            </div>
                        }

                        return undefined;
                    })}
                </div>
            })
            }
            {errors.section ? <Notification kind={KIND.negative}>{() => errors.section}</Notification> : null}
        </div>
        <div style={{ maxWidth: '700px', width: '95%', margin: 'auto', textAlign: 'right' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'right', margin: '15px 0px' }}>
                {goBack !== undefined ? <ArrowButton
                    action={() => {
                        // if (validate()) {
                        goBack()
                        // }
                    }}
                    disabled={false}
                    type='left'
                /> : undefined}
                <ArrowButton
                    message={'Next'}
                    action={() => {
                        if (validate()) {
                            finishFlow()
                        }
                    }}
                    disabled={false}
                    type='right'
                />
                <ConfirmChangeModal />
            </div>
        </div>
    </div>
}

export const FlowWithModal = ({ flowInfo, questionSections, initialValue, handleOpen, handleFinish }) => {
    initialValue = initialValue ? initialValue : {};
    const [answer, setAnswer] = React.useState();
    const [isOpen, setIsOpen] = React.useState(false);
    let stringifiedValue = JSON.stringify(initialValue);
    // eslint-disable-next-line 
    React.useEffect(() => setAnswer(initialValue), [stringifiedValue]);
    const handleClose = () => {
        if (answer !== initialValue) setAnswer(initialValue);
        setIsOpen(false);
    }

    const handleSetAnswer = (key, value) => {
        setAnswer({
            ...answer,
            [key]: value,
        });
    }

    return (
        <>
            <Button onClick={() => {
                if (handleOpen) handleOpen();
                setIsOpen(true);
            }}>{flowInfo.buttonText}</Button>
            <Modal onClose={handleClose} isOpen={isOpen} unstable_ModalBackdropScroll>
                <ModalHeader>{flowInfo.name}</ModalHeader>
                <ModalBody>
                    <Flow questionSections={questionSections} answers={answer} finishFlow={() => {
                        handleFinish(answer);
                        handleClose();
                    }} setAnswer={handleSetAnswer} flowMode />
                </ModalBody>
                <ModalFooter>
                    <ModalButton kind="tertiary" onClick={handleClose}>
                        Cancel
                </ModalButton>
                </ModalFooter>
            </Modal>
        </>
    );
}

export const ConnectedFlow = connect((state, ownProps) => {
    return {
        answers: state.userData.answers,
    };
}, {
    setAnswer,
    setAllAnswers,
    startConfirmModal
})(Flow);